/**
 * Hybrid TokenInput component.
 * Automatically chooses between the Tailwind/CSS-class implementation (preferred,
 * no runtime CSS-in-JS) and the styled-components implementation, based on props.
 *
 * Routes to the styled-components path when a styled-system prop is present
 * (hasStyledProps) or the component was extended with styled() (isStyledExtension).
 * The container only applies the COMMON styled-system composition, so — unlike
 * Avatar — no BORDER-mixin check is needed. Otherwise renders the Tailwind path.
 *
 * Both leaf implementations are class components, so the forwarded `ref` is
 * passed through the `containerRef` prop (a class `ref` would resolve to the
 * component instance, not the root Container DOM node). Radix UI's `asChild`
 * pattern — used by seeds-react-menu — relies on this ref being a DOM element
 * for `.contains()` checks.
 */
import * as React from "react";
import {
  hasStyledProps,
  isStyledExtension,
} from "@sproutsocial/seeds-react-system-props";
import type { TypeTokenInputProps } from "./TokenInputTypes";
import TokenInputStyled from "./TokenInputStyled";
import TokenInputTailwind from "./TokenInputTailwind";

const TokenInputHybrid = React.forwardRef<HTMLDivElement, TypeTokenInputProps>(
  (props, ref) => {
    if (hasStyledProps(props) || isStyledExtension(props)) {
      return <TokenInputStyled {...props} containerRef={ref} />;
    }

    // Use Tailwind version (preferred - better performance)
    return <TokenInputTailwind {...props} containerRef={ref} />;
  }
);

TokenInputHybrid.displayName = "TokenInput";

export default TokenInputHybrid;
